import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { reportDynatraceError, trackDynatraceEvent } from '@/core/services/dynatraceService';
import { getValue } from '@/core/services/translationService';
import { parsePhoneNumber as parsePhoneNumberUtil } from '@/core/utils/countryCodes';
import { otpService, SendEmailOtpRequest, SendOtpRequest, SendSmsOtpRequest, VerifyOtpRegistrationRequest, VerifyOtpRequest } from '@/modules/onboarding/services/otpService';
import { useCallback, useEffect, useState } from 'react';

export type OtpType = 'email' | 'phone';

interface UseOTPOptions {
    type: OtpType;
    contactInfo: string; // email or phone number
    autoSend?: boolean; // Automatically send OTP on mount
    updateType?: 'Primary' | 'Secondary'; // Update type: Primary (default) or Secondary (for communication email from profile)
    isRegistration?: boolean; // If true, use verify-otp-registration endpoint instead of verify-otp
    agencyType?: 'domestic' | 'international'; // Optional agency type for analytics
    sectionName?: string; // Optional section name for analytics
}

interface UseOTPResult {
    transactionId: string | null;
    isLoading: boolean; // Loading state for verification
    error: string | null;
    isVerified: boolean;
    sendOtp: (contactOverride?: string) => Promise<void>;
    verifyOtp: (otp: string) => Promise<boolean>;
    resendOtp: () => Promise<void>;
    clearError: () => void;
    resetVerification: () => void; // Reset verification state (for edit button)
}

/**
 * Custom hook for OTP management
 * Handles sending and verifying OTP for email and phone
 */
export const useOTP = ({
    type,
    contactInfo,
    autoSend = true,
    updateType = 'Primary',
    isRegistration = false,
    agencyType,
    sectionName,
}: UseOTPOptions): UseOTPResult => {
    const { currentLanguage } = useI18nContext();
    const [transactionId, setTransactionId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false); // For verification
    const [error, setError] = useState<string | null>(null);
    const [isVerified, setIsVerified] = useState(false);
    const [pendingOtp, setPendingOtp] = useState<string | null>(null); // Store OTP if entered before transactionId is available

    // customer_id is no longer required for OTP APIs.

    /**
     * Parse phone number to extract country code and mobile number
     * Uses centralized country codes utility as single source of truth
     */
    const parsePhoneNumber = useCallback((phone: string): { countryCode: string; mobileNumber: string } => {
        const parsed = parsePhoneNumberUtil(phone);
        // Return format expected by this hook (numeric code without +)
        return {
            countryCode: parsed.code.replace('+', ''),
            mobileNumber: parsed.local,
        };
    }, []);

    const analyticsBase = {
        p_screen: 'otp',
        p_sectionName: sectionName,
        p_agencyType: agencyType,
    };

    /**
     * Send OTP
     * @param contactOverride - Optional contact info to override the hook's contactInfo (useful when contact is submitted)
     */
    const sendOtp = useCallback(async (contactOverride?: string): Promise<void> => {
        // Use override if provided, otherwise use hook's contactInfo
        const contactToUse = contactOverride || contactInfo;

        if (!contactToUse) {
            const errorMsg = type === 'email' ? 'Email is required' : 'Phone number is required';
            logger.error('useOTP', errorMsg, {
                contactOverride,
                contactInfo,
                contactToUse,
            });
            setError(errorMsg);
            trackDynatraceEvent({
                ...analyticsBase,
                p_actionName: 'Send OTP',
                p_failedMessage: errorMsg,
                eventType: 'error',
            });
            return;
        }

        setError(null);

        try {
            let request: SendOtpRequest;

            if (type === 'email') {
                request = {
                    commType: 'Email',
                    email: contactToUse,
                } as SendEmailOtpRequest;
            } else {
                const { countryCode, mobileNumber } = parsePhoneNumber(contactToUse);
                // mobileNumber should include the country code
                const fullMobileNumber = `${countryCode}${mobileNumber}`;
                request = {
                    commType: 'SMS',
                    country_code: countryCode,
                    mobileNumber: fullMobileNumber,
                } as SendSmsOtpRequest;
            }

            const response = await otpService.sendOtp(request);
            const newTransactionId = response.details.transactionId;
            setTransactionId(newTransactionId);
            logger.info('useOTP', 'OTP sent successfully', {
                type,
                transactionId: newTransactionId,
                contactUsed: contactToUse,
            });
            trackDynatraceEvent({
                ...analyticsBase,
                p_actionName: 'Send OTP',
                eventType: 'action',
            });
        } catch (err: any) {
            const errorMessage = err?.response?.data?.error || err?.message || 'Failed to send OTP';
            logger.error('useOTP', 'Failed to send OTP', err);
            setError(errorMessage);
            setTransactionId(null);
            // Clear pending OTP if send failed
            setPendingOtp(null);
            trackDynatraceEvent({
                ...analyticsBase,
                p_actionName: 'Send OTP',
                p_failedMessage: errorMessage,
                eventType: 'error',
            });
            reportDynatraceError(errorMessage, err);
        }
    }, [type, contactInfo, parsePhoneNumber, isRegistration, analyticsBase]);

    /**
     * Internal verify OTP function (used by both verifyOtp and auto-verify)
     */
    const verifyOtpInternal = useCallback(async (otp: string, txId: string): Promise<boolean> => {
        if (!otp || otp.length !== 4) {
            const errorMsg = 'Invalid OTP. Please enter a 4-digit code.';
            logger.error('useOTP', errorMsg);
            setError(errorMsg);
            trackDynatraceEvent({
                ...analyticsBase,
                p_actionName: 'Verify OTP',
                p_failedMessage: errorMsg,
                eventType: 'error',
            });
            return false;
        }

        setIsLoading(true);
        setError(null);

        try {
            // For registration flow, use verify-otp-registration endpoint
            if (isRegistration) {
                const request: VerifyOtpRegistrationRequest = {
                    otp,
                    transactionId: txId,
                };

                // Add type-specific fields (same as regular verify-otp)
                if (type === 'phone') {
                    const { countryCode, mobileNumber } = parsePhoneNumber(contactInfo);
                    // mobileNumber should include the country code
                    const fullMobileNumber = `${countryCode}${mobileNumber}`;
                    request.country_code = countryCode;
                    request.mobileNumber = fullMobileNumber;
                } else {
                    // Email type
                    request.email = contactInfo;
                }

                const response = await otpService.verifyOtpRegistration(request);
                const serverMessage = response.details.message || '';
                const messageLower = serverMessage.toLowerCase();
                
                // Check if verification was successful
                const isSuccessful = messageLower.includes('successfully') && 
                                    !messageLower.includes('unsuccessfully');
                
                // Check if message indicates expired/timeout
                const isExpired = messageLower.includes('expired') || 
                                messageLower.includes('timeout');
                
                // Check if message indicates unsuccessful/wrong OTP (but not expired)
                const isUnsuccessful = (messageLower.includes('unsuccessfully') || 
                                       messageLower.includes('wrong otp') ||
                                       (messageLower.includes('wrong') && messageLower.includes('otp'))) && 
                                       !isExpired;

                // If message doesn't match our known patterns (successful, unsuccessful, expired), show server message
                if (!isSuccessful && !isUnsuccessful && !isExpired && serverMessage) {
                    setError(serverMessage);
                    logger.warn('useOTP', 'OTP verification - unknown message pattern, showing server message', {
                        transactionId: txId,
                        message: serverMessage,
                    });
                    setIsVerified(false);
                    trackDynatraceEvent({
                        ...analyticsBase,
                        p_actionName: 'Verify OTP',
                        p_failedMessage: serverMessage,
                        eventType: 'error',
                    });
                    reportDynatraceError(serverMessage);
                    return false;
                }

                // Check for unsuccessful verification (wrong OTP but correct transactionId)
                if (isUnsuccessful) {
                    // Use translation for invalid OTP message
                    const errorMsg = getValue(currentLanguage, 'otp.invalidOTPPleaseTryAgain') || 'Invalid OTP. Please try again.';
                    setError(errorMsg);
                    logger.warn('useOTP', 'OTP verification failed - invalid OTP', {
                        transactionId: txId,
                        message: serverMessage,
                    });
                    setIsVerified(false);
                    trackDynatraceEvent({
                        ...analyticsBase,
                        p_actionName: 'Verify OTP',
                        p_failedMessage: errorMsg,
                        eventType: 'error',
                    });
                    return false;
                }
                
                // Check for expired/timeout messages
                if (isExpired && !isSuccessful) {
                    // Use translation for expired/timeout messages
                    const errorMsg = getValue(currentLanguage, 'otp.otpExpired') || 'OTP expired. Tap Resend OTP to get a new one.';
                    setError(errorMsg);
                    logger.warn('useOTP', 'OTP verification failed - OTP expired/timeout', {
                        transactionId: txId,
                        message: serverMessage,
                    });
                    setIsVerified(false);
                    trackDynatraceEvent({
                        ...analyticsBase,
                        p_actionName: 'Verify OTP',
                        p_failedMessage: errorMsg,
                        eventType: 'error',
                    });
                    return false;
                }

                // Success case
                if (isSuccessful) {
                    logger.info('useOTP', 'OTP verified successfully (registration)', { transactionId: txId });
                    setIsVerified(true);
                    trackDynatraceEvent({
                        ...analyticsBase,
                        p_actionName: 'Verify OTP',
                        eventType: 'action',
                    });
                    return true;
                }

                // Fallback: if status is success but message is unclear, assume success
                if (response.status === 'success') {
                    logger.info('useOTP', 'OTP verified (registration, fallback success)', { 
                        transactionId: txId,
                        message: serverMessage,
                    });
                    setIsVerified(true);
                    trackDynatraceEvent({
                        ...analyticsBase,
                        p_actionName: 'Verify OTP',
                        eventType: 'action',
                    });
                    return true;
                }

                // Unknown status
                const errorMsg = 'Failed to verify OTP';
                setError(errorMsg);
                logger.warn('useOTP', 'OTP verification failed - unknown status', {
                    transactionId: txId,
                    message: serverMessage,
                });
                setIsVerified(false);
                trackDynatraceEvent({
                    ...analyticsBase,
                    p_actionName: 'Verify OTP',
                    p_failedMessage: errorMsg,
                    eventType: 'error',
                });
                return false;
            }

            // Build verify OTP request with all required fields (no customer_id)
            const request: VerifyOtpRequest = {
                otp,
                transactionId: txId,
                update_type: updateType,
            };

            // Add type-specific fields
            if (type === 'phone') {
                const { countryCode, mobileNumber } = parsePhoneNumber(contactInfo);
                // mobileNumber should include the country code
                const fullMobileNumber = `${countryCode}${mobileNumber}`;
                request.country_code = countryCode;
                request.mobileNumber = fullMobileNumber;
            } else {
                // Email type
                request.email = contactInfo;
            }

            const response = await otpService.verifyOtp(request);
            const serverMessage = response.details.message || '';
            const messageLower = serverMessage.toLowerCase();
            
            // Check if verification was successful
            const isSuccessful = messageLower.includes('successfully') && 
                                !messageLower.includes('unsuccessfully');
            
            // Check if message indicates expired/timeout
            const isExpired = messageLower.includes('expired') || 
                            messageLower.includes('timeout');
            
            // Check if message indicates unsuccessful/wrong OTP (but not expired)
            const isUnsuccessful = (messageLower.includes('unsuccessfully') || 
                                   messageLower.includes('wrong otp') ||
                                   (messageLower.includes('wrong') && messageLower.includes('otp'))) && 
                                   !isExpired;

            // If message doesn't match our known patterns (successful, unsuccessful, expired), show server message
            if (!isSuccessful && !isUnsuccessful && !isExpired && serverMessage) {
                setError(serverMessage);
                logger.warn('useOTP', 'OTP verification - unknown message pattern, showing server message', {
                    transactionId: txId,
                    message: serverMessage,
                });
                setIsVerified(false);
                trackDynatraceEvent({
                    ...analyticsBase,
                    p_actionName: 'Verify OTP',
                    p_failedMessage: serverMessage,
                    eventType: 'error',
                });
                reportDynatraceError(serverMessage);
                return false;
            }

            // Check for unsuccessful verification (wrong OTP but correct transactionId)
            if (isUnsuccessful) {
                // Use translation for invalid OTP message
                const errorMsg = getValue(currentLanguage, 'otp.invalidOTPPleaseTryAgain') || 'Invalid OTP. Please try again.';
                setError(errorMsg);
                logger.warn('useOTP', 'OTP verification failed - invalid OTP', {
                    transactionId: txId,
                    message: serverMessage,
                });
                setIsVerified(false);
                trackDynatraceEvent({
                    ...analyticsBase,
                    p_actionName: 'Verify OTP',
                    p_failedMessage: errorMsg,
                    eventType: 'error',
                });
                return false;
            }
            
            // Check for expired/timeout messages
            if (isExpired && !isSuccessful) {
                // Use translation for expired/timeout messages
                const errorMsg = getValue(currentLanguage, 'otp.otpExpired') || 'OTP expired. Tap Resend OTP to get a new one.';
                setError(errorMsg);
                logger.warn('useOTP', 'OTP verification failed - OTP expired/timeout', {
                    transactionId: txId,
                    message: serverMessage,
                });
                setIsVerified(false);
                trackDynatraceEvent({
                    ...analyticsBase,
                    p_actionName: 'Verify OTP',
                    p_failedMessage: errorMsg,
                    eventType: 'error',
                });
                return false;
            }

            // Success case
            if (isSuccessful) {
                logger.info('useOTP', 'OTP verified successfully', { transactionId: txId });
                setIsVerified(true);
                trackDynatraceEvent({
                    ...analyticsBase,
                    p_actionName: 'Verify OTP',
                    eventType: 'action',
                });
                return true;
            }

            // Fallback: if status is success but message is unclear, assume success
            if (response.status === 'success') {
                logger.info('useOTP', 'OTP verified (fallback success)', { 
                    transactionId: txId,
                    message: serverMessage,
                });
                setIsVerified(true);
                trackDynatraceEvent({
                    ...analyticsBase,
                    p_actionName: 'Verify OTP',
                    eventType: 'action',
                });
                return true;
            }

            // Unknown status
            const errorMsg = 'Failed to verify OTP';
            setError(errorMsg);
            logger.warn('useOTP', 'OTP verification failed - unknown status', {
                transactionId: txId,
                message: serverMessage,
            });
            setIsVerified(false);
            trackDynatraceEvent({
                ...analyticsBase,
                p_actionName: 'Verify OTP',
                p_failedMessage: errorMsg,
                eventType: 'error',
            });
            return false;
        } catch (err: any) {
            const errorMessage = err?.response?.data?.error || err?.message || 'Failed to verify OTP';
            logger.error('useOTP', 'Failed to verify OTP', err);
            setError(errorMessage);
            setIsVerified(false);
            trackDynatraceEvent({
                ...analyticsBase,
                p_actionName: 'Verify OTP',
                p_failedMessage: errorMessage,
                eventType: 'error',
            });
            reportDynatraceError(errorMessage, err);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [type, contactInfo, updateType, parsePhoneNumber, currentLanguage, isRegistration, analyticsBase]);

    /**
     * Verify OTP (public API)
     * If transactionId is not available yet, store OTP as pending
     */
    const verifyOtp = useCallback(async (otp: string): Promise<boolean> => {
        if (!otp || otp.length !== 4) {
            const errorMsg = 'Invalid OTP. Please enter a 4-digit code.';
            logger.error('useOTP', errorMsg);
            setError(errorMsg);
            trackDynatraceEvent({
                ...analyticsBase,
                p_actionName: 'Verify OTP',
                p_failedMessage: errorMsg,
                eventType: 'error',
            });
            return false;
        }

        // If transactionId is available, verify immediately
        if (transactionId) {
            return await verifyOtpInternal(otp, transactionId);
        }

        // If transactionId is not available yet, store OTP as pending
        logger.info('useOTP', 'Transaction ID not available yet, storing OTP as pending', { otp });
        setPendingOtp(otp);
        return false; // Return false since verification hasn't happened yet
    }, [transactionId, verifyOtpInternal]);

    /**
     * Resend OTP (same as send, but clears previous transaction)
     */
    const resendOtp = useCallback(async (): Promise<void> => {
        setTransactionId(null);
        setIsVerified(false);
        setError(null);
        setPendingOtp(null); // Clear any pending OTP
        await sendOtp();
        trackDynatraceEvent({
            ...analyticsBase,
            p_actionName: 'Resend OTP',
            eventType: 'action',
        });
    }, [sendOtp, analyticsBase]);

    /**
     * Clear error
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Reset verification state (for edit button)
     */
    const resetVerification = useCallback(() => {
        setIsVerified(false);
        setError(null);
        setPendingOtp(null);
        logger.debug('useOTP', 'Verification state reset');
    }, []);

    // Auto-verify pending OTP when transactionId becomes available
    useEffect(() => {
        if (transactionId && pendingOtp && pendingOtp.length === 4 && !isLoading) {
            logger.info('useOTP', 'Transaction ID received, auto-verifying pending OTP', {
                pendingOtp,
                transactionId,
            });
            // Auto-verify the pending OTP
            verifyOtpInternal(pendingOtp, transactionId).then(() => {
                setPendingOtp(null); // Clear pending OTP after verification attempt
            });
        }
    }, [transactionId, pendingOtp, isLoading, verifyOtpInternal]);

    // Auto-send OTP on mount if enabled (non-blocking)
    useEffect(() => {
        const canAutoSend =
            autoSend && contactInfo && !transactionId && !isLoading;
        
        if (canAutoSend) {
            // Don't await - let it run in background, keyboard appears immediately
            sendOtp().catch((err) => {
                logger.error('useOTP', 'Failed to auto-send OTP', err);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSend, contactInfo, isRegistration]); // Only depend on these, not sendOtp to avoid infinite loops

    return {
        transactionId,
        isLoading,
        error,
        isVerified,
        sendOtp,
        verifyOtp,
        resendOtp,
        clearError,
        resetVerification,
    };
};

